import type { DashboardTimeRange } from "./types";

export type ScheduledTimeRange = Extract<DashboardTimeRange, { mode: "scheduled" }>;

const DAY = 86_400_000;

function timeOffset(time: string): number {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) return NaN;
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 3_600_000 + minutes * 60_000;
}

function monthDate(year: number, month: number, day: number): number {
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return Date.UTC(year, month, Math.min(day, lastDay));
}

export function scheduledBoundaries(
  schedule: ScheduledTimeRange["schedule"],
  referenceTime: number,
): { previous: number; next: number } | null {
  const offset = timeOffset(schedule.time);
  if (!Number.isFinite(referenceTime) || !Number.isFinite(offset)) return null;
  const today = Math.floor(referenceTime / DAY) * DAY;
  let previous: number;
  let next: number;
  if (schedule.frequency === "weekly") {
    if (!Number.isInteger(schedule.weekday) || schedule.weekday < 0 || schedule.weekday > 6)
      return null;
    const weekday = new Date(today).getUTCDay();
    previous = today - ((weekday - schedule.weekday + 7) % 7) * DAY + offset;
    if (previous > referenceTime) previous -= 7 * DAY;
    next = previous + 7 * DAY;
  } else if (schedule.frequency === "monthly") {
    if (!Number.isInteger(schedule.day) || schedule.day < 1 || schedule.day > 31) return null;
    const date = new Date(today);
    const year = date.getUTCFullYear();
    let month = date.getUTCMonth();
    previous = monthDate(year, month, schedule.day) + offset;
    if (previous > referenceTime) previous = monthDate(year, --month, schedule.day) + offset;
    next = monthDate(year, month + 1, schedule.day) + offset;
  } else {
    if (
      !Number.isInteger(schedule.everyDays) ||
      schedule.everyDays < 1 ||
      schedule.everyDays > 3660 ||
      !/^\d{4}-\d{2}-\d{2}$/.test(schedule.anchorDate)
    )
      return null;
    const anchor = Date.parse(`${schedule.anchorDate}T00:00:00.000Z`);
    if (
      !Number.isFinite(anchor) ||
      new Date(anchor).toISOString().slice(0, 10) !== schedule.anchorDate
    )
      return null;
    const first = anchor + offset;
    const interval = schedule.everyDays * DAY;
    previous = first + Math.floor((referenceTime - first) / interval) * interval;
    next = previous + interval;
  }
  return { previous, next };
}

export function resolveScheduledTimeRange(
  policy: ScheduledTimeRange,
  referenceTime: number,
): { from: number; to: number } | null {
  const boundaries = scheduledBoundaries(policy.schedule, referenceTime);
  const cutoffTime = timeOffset(policy.cutoff.time);
  if (
    !boundaries ||
    !Number.isInteger(policy.amount) ||
    policy.amount < 1 ||
    policy.amount > 3660 ||
    !Number.isInteger(policy.cutoff.daysBefore) ||
    policy.cutoff.daysBefore < 0 ||
    policy.cutoff.daysBefore > 3660 ||
    !Number.isFinite(cutoffTime)
  )
    return null;
  const to =
    Math.floor(boundaries.previous / DAY) * DAY - policy.cutoff.daysBefore * DAY + cutoffTime;
  if (to > boundaries.previous) return null;
  let from: number;
  if (policy.unit === "month") {
    const end = new Date(to);
    from =
      monthDate(end.getUTCFullYear(), end.getUTCMonth() - policy.amount, end.getUTCDate()) +
      (to - Math.floor(to / DAY) * DAY);
  } else {
    from = to - policy.amount * DAY;
  }
  return { from, to };
}
